import type {
  SaveItem,
  ItemChanges,
  ItemWithId,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { useCallback } from "react"

import { useUpsertCollectionItem } from "src/hooks/rxdb"
import type { CollectionKey } from "src/types"
import { noticeError } from "src/utils"

export function useUpdateCollectionItem<Key extends string, Item extends ItemWithId, TransformedItem = Item>(
  collectionKey: CollectionKey,
  responseKey: Key,
  useSaveItem: () => SaveItem<Item, Key> | undefined,
  transform: (i: Item) => TransformedItem,
) {
  const saveItem = useSaveItem()
  const upsertCollectionItem = useUpsertCollectionItem<TransformedItem>(collectionKey)

  return useCallback(
    async (updateItem: ItemChanges<Item>) => {
      try {
        if (!saveItem) {
          throw new Error(`cannot update ${responseKey} for unauthorized user`)
        }

        const response = await saveItem(updateItem)
        const item = response.data[responseKey]
        await upsertCollectionItem(transform(item))
        return response
      } catch (e) {
        noticeError(
          e,
          "UpdateCollectionItem",
          {
            [responseKey]: updateItem,
            collectionItem: responseKey,
            collectionKey,
          },
        )

        throw e
      }
    },
    [
      collectionKey,
      responseKey,
      saveItem,
      transform,
      upsertCollectionItem,
    ],
  )
}
